// src/security/v2/useV2ProjectRoles.ts
//
// DEV-1033 — Project-scope parallel to useV2CommunityRoles (DEV-971).
// Derive each project's effective capability set for the current user via
// the V2 project evaluator. Backs the synchronous canViewProject /
// canEditProjectContent / canAdminProject checks in securityAdapter.ts so
// V2-issued grants (via the Permissions panel, once V1 admin UI retires)
// show up alongside legacy project_acl rows during the shadow-mode window.
//
// **Sysadmin behavior:** `isSystemAdmin` is forced to `false` regardless
// of the caller's actual sysadmin status. The synchronous gate functions
// have their own sysadmin fast-path (`isUserSystemAdmin`) that short-
// circuits BEFORE consulting this cache, so feeding `true` here would
// double-count. The hook's job is "what V2 grants does this non-sysadmin
// user have?"; sysadmin is a separate axis.
//
// **Cost:** O(projects × grants) per render trigger. Catalog cached at
// module scope; grants refetched per call. For Skip's working dataset
// (~50–200 projects) this runs sub-millisecond.
//
// **Module-scope cache:** the hook writes resolved caps into
// `__v2ProjectCapsCache` keyed by userGuid → Map<projectGuid, Set<cap>>.
// `getV2ProjectCapsForUser(userGuid)` is the synchronous read path used
// by securityAdapter.ts gate functions. The cache persists across
// re-renders within a session; it's invalidated implicitly when the hook
// re-runs (different userGuid, new grants, etc.) and explicitly via
// `clearV2ProjectCapsCache()` for testing.

import { useEffect, useState } from "react";
import { permissionsApiV2 } from "./apiClient";
import { evaluateSecurityForUserV2 } from "./evaluator";
import type {
  CapabilityDto,
  CapabilityName,
  EvaluationInputs,
  GUID,
  MyGrantsResponse,
  PermissionSetDto,
} from "./types";

/**
 * Minimal shape required by the hook for each project. Callers transform
 * their own data into this list — keeps the hook decoupled from the
 * upstream Data row shape (which has decades of accreted fields).
 */
export interface ProjectForEvaluation {
  projectGuid: GUID;
  visibility: "public" | "private";
  /**
   * Collection GUIDs this project belongs to. Empty array if none —
   * collection-cascade rule contributes nothing in that case. Callers
   * who don't have collection membership wired (most do not yet) can
   * safely pass `[]`; the evaluator just skips Rule 4.
   */
  collectionGuids: GUID[];
}

interface UseV2ProjectRolesArgs {
  userGuid: GUID;
  /** Set true when the user has TOTP enrolled. Reserved for parity with
   *  useV2CommunityRoles — the project evaluator does not yet apply the
   *  requires_2fa gate (no project-scope bundle is flagged requires2fa
   *  in v1), but threading it now means turning it on later is a no-op. */
  is2FAEnabled?: boolean;
  projects?: ProjectForEvaluation[];
}

interface UseV2ProjectRolesResult {
  /** Map from upper-cased project GUID → effective capability set.
   *  Projects for which the evaluator returns zero capabilities are
   *  omitted from the map. */
  capsByProject: Map<string, Set<CapabilityName>>;
  loading: boolean;
  error: string | null;
}

// ── Module-scope catalog cache ──────────────────────────────────────
// Capabilities + bundles are stable across users and rarely change.
// Same convention as useV2CommunityRoles + useShadowPermissions.
let _catalog: {
  capabilities: CapabilityDto[];
  permissionSets: PermissionSetDto[];
} | null = null;

async function loadCatalog(): Promise<typeof _catalog> {
  if (_catalog) return _catalog;
  const [capabilities, permissionSets] = await Promise.all([
    permissionsApiV2.capabilities(),
    permissionsApiV2.permissionSets(),
  ]);
  _catalog = { capabilities, permissionSets };
  return _catalog;
}

// ── Module-scope capability cache for the synchronous read path ────
//
// The synchronous gate functions in securityAdapter.ts (canViewProject,
// canEditProjectContent, canAdminProject) consult this map BEFORE
// falling through to the V1 evaluator. Keyed by upper-cased userGuid →
// Map<upper-cased projectGuid, Set<CapabilityName>>.
//
// Populated by `useV2ProjectRoles` on every resolved render.
// `getV2ProjectCapsForUser(userGuid)` is the read entry point.

const __v2ProjectCapsCache = new Map<string, Map<string, Set<CapabilityName>>>();

/**
 * Synchronous read of the V2 capability cache for a given user. Returns
 * `undefined` when the V2 hook hasn't yet resolved for that user — the
 * caller should treat that as "no V2 grants known yet" and fall through
 * to the V1 read path.
 */
export function getV2ProjectCapsForUser(
  userGuid: GUID | null | undefined,
): Map<string, Set<CapabilityName>> | undefined {
  if (!userGuid) return undefined;
  return __v2ProjectCapsCache.get(String(userGuid).toUpperCase());
}

/**
 * Test helper: drop the module-scope cache. Production code should not
 * call this — `useV2ProjectRoles` rewrites the cache on every successful
 * resolution.
 */
export function clearV2ProjectCapsCache(): void {
  __v2ProjectCapsCache.clear();
}

/**
 * Compute each project's effective capabilities for the current user
 * via the V2 project evaluator. Hook surface mirrors
 * `useV2CommunityRoles`; see that file for the shared design rationale.
 *
 * The hook ALSO writes results into a module-scope cache that the
 * synchronous gate functions in securityAdapter.ts consult. This lets
 * the 22+ existing canEditProjectContent / canAdminProject call sites
 * continue to work unchanged — they gain a V2 fast-path automatically
 * once the hook mounts in App.tsx.
 *
 * **Returns** the same Map structure for direct consumption by pages
 * that prefer to read capabilities inline (matching the
 * useV2CommunityRoles consumer pattern).
 */
export function useV2ProjectRoles({
  userGuid,
  is2FAEnabled = false,
  projects,
}: UseV2ProjectRolesArgs): UseV2ProjectRolesResult {
  void is2FAEnabled; // reserved for parity; no project bundle uses it yet
  const [state, setState] = useState<UseV2ProjectRolesResult>({
    capsByProject: new Map(),
    loading: true,
    error: null,
  });

  // Stable dep keys — array references would re-fire every render.
  // Project count + a comma-joined GUID hash detects actual changes
  // without forcing reference stability on the caller.
  const projectsKey = (projects || [])
    .map((p) => `${p.projectGuid}:${p.visibility}:${p.collectionGuids.length}`)
    .join(",");

  useEffect(() => {
    let cancelled = false;

    if (!userGuid) {
      // Drop any cached entries for the empty-user case — prevents
      // stale caps from a previous user lingering after sign-out.
      __v2ProjectCapsCache.clear();
      setState({ capsByProject: new Map(), loading: false, error: null });
      return;
    }

    (async () => {
      try {
        const catalog = await loadCatalog();
        if (!catalog || cancelled) return;

        let myGrants: MyGrantsResponse;
        try {
          myGrants = await permissionsApiV2.myGrants();
        } catch {
          // Network / auth failure → no grants. Empty map; callers fall
          // through to the V1 read path.
          myGrants = {
            userGuid,
            activeSlotGuids: [],
            directGrants: [],
            slotGrants: [],
          };
        }
        if (cancelled) return;

        const result = new Map<string, Set<CapabilityName>>();

        for (const p of projects || []) {
          const guid = String(p.projectGuid || "").toUpperCase();
          if (!guid) continue;

          const inputs: EvaluationInputs = {
            userGuid,
            isSystemAdmin: false, // intentional — see jsdoc above
            capabilities: catalog.capabilities,
            permissionSets: catalog.permissionSets,
            myGrants,
            collectionGuidsForProject: p.collectionGuids || [],
            projectVisibility: p.visibility,
          };

          const evalRes = evaluateSecurityForUserV2(guid, inputs);
          if (evalRes.capabilities.size > 0) {
            result.set(guid, evalRes.capabilities);
          }
        }

        if (cancelled) return;

        // Rewrite the cache entry for this user. Other users' entries
        // (if any) are left alone — multi-user simulation paths in
        // debug mode could conceivably populate multiple entries.
        __v2ProjectCapsCache.set(String(userGuid).toUpperCase(), result);

        setState({ capsByProject: result, loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            capsByProject: new Map(),
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGuid, is2FAEnabled, projectsKey]);

  return state;
}

// ── Capability → boolean mapping for the gate functions ────────────
//
// The V1 project gates have coarse semantics — canViewProject answers
// "any view access at all?", canEditProjectContent answers "owner_admin
// or contributor?", canAdminProject answers "owner_admin?". V2 caps are
// granular per area (about:edit, impact:edit, risks:edit, …). These
// mappings define the boolean reduction for each gate.
//
// Keep these aligned with the V1 semantics so the shadow-mode addition
// is purely additive — V2 grants unlock the same gates V1 unlocks.

/** Floor: any view access. `about:view` is held by every assignable
 *  project bundle, so it's the equivalent of "I have a role here." */
export function v2CanViewProject(caps: Set<CapabilityName>): boolean {
  return caps.has("about:view");
}

/** Equivalent of V1 contributor-or-higher: holding any content edit
 *  capability OR project:admin (which transitively grants every edit). */
export function v2CanEditProjectContent(caps: Set<CapabilityName>): boolean {
  return (
    caps.has("project:admin") ||
    caps.has("about:edit") ||
    caps.has("impact:edit") ||
    caps.has("budget:edit") ||
    caps.has("contributions:edit") ||
    caps.has("expert:edit") ||
    caps.has("performance:edit") ||
    caps.has("resources:edit") ||
    caps.has("risks:edit") ||
    caps.has("schedule:edit") ||
    caps.has("team:edit")
  );
}

/** Equivalent of V1 owner_admin: `project:admin` is the dedicated cap.
 *  `project:settings` is held by admin-tier bundles as well; checking
 *  both means project-settings authority counts as admin even on
 *  hypothetical future bundles that grant settings without project:admin. */
export function v2CanAdminProject(caps: Set<CapabilityName>): boolean {
  return caps.has("project:admin") || caps.has("project:settings");
}
